import React from "react";
import { ICellRendererParams } from "ag-grid-community";
import { Text } from "@jsq/ds/core";
import { type ExtensionFieldValueObjectType } from "~/graphql/supergraphTypes";
import { EXTENSION_FIELD_TYPE_IDS } from "../../../utils/extensionFieldUtils";
import { CurrencyFieldRenderer } from "./CurrencyFieldRenderer";
import { DateFieldRenderer } from "./DateFieldRenderer";
import { EnumerationFieldRenderer } from "./EnumerationFieldRenderer";
import { NumberFieldRenderer } from "./NumberFieldRenderer";
import { PercentileFieldRenderer } from "./PercentileFieldRenderer";
import { TextFieldRenderer } from "./TextFieldRenderer";

interface ExtensionFieldCellRendererParams extends ICellRendererParams {
  fieldType: number;
  enumValues?: Array<{
    __typename?: "ExtensionFieldEnumObjectType";
    id: string;
    enumName: string;
    enumValueCode: string;
    enumValueLabel: string;
  }>;
  fieldId: string;
}

/**
 * Cell renderer for extension field values - routes to appropriate specialized renderer based on field type
 */
export const ExtensionFieldCellRenderer: React.FC<ExtensionFieldCellRendererParams> = (
  params,
) => {
  const { fieldType, enumValues } = params;
  const value = params.value as ExtensionFieldValueObjectType | null;

  if (value === null || value === undefined) {
    return <Text variant="secondary-sm">—</Text>;
  }

  // Handle case where value is not an object (fallback safety)
  if (typeof value !== "object") {
    return (
      <Text variant="secondary-sm" color="danger">
        Invalid value type
      </Text>
    );
  }

  // Route to appropriate renderer based on field type
  try {
    switch (fieldType) {
      case EXTENSION_FIELD_TYPE_IDS.TEXT:
        return <TextFieldRenderer value={value} />;

      case EXTENSION_FIELD_TYPE_IDS.INTEGER:
        return <NumberFieldRenderer value={value} fieldType="integer" />;

      case EXTENSION_FIELD_TYPE_IDS.FLOAT:
        return <NumberFieldRenderer value={value} fieldType="float" />;

      case EXTENSION_FIELD_TYPE_IDS.DATE:
        return <DateFieldRenderer value={value} includeTime={false} />;

      case EXTENSION_FIELD_TYPE_IDS.CURRENCY_USD:
        return <CurrencyFieldRenderer value={value} />;

      case EXTENSION_FIELD_TYPE_IDS.ENUMERATION:
        return <EnumerationFieldRenderer value={value} enumValues={enumValues || []} />;

      case EXTENSION_FIELD_TYPE_IDS.PERCENTILE:
        return <PercentileFieldRenderer value={value} />;

      default:
        return (
          <Text variant="secondary-sm" color="danger">
            Unknown field type: {fieldType}
          </Text>
        );
    }
  } catch {
    return (
      <Text variant="secondary-sm" color="danger">
        Render error
      </Text>
    );
  }
};

ExtensionFieldCellRenderer.displayName = "ExtensionFieldCellRenderer";

export default ExtensionFieldCellRenderer;
